// src/hooks/useSceneTesisatSync.js
//
// Phase A.3: Scene → tesisatSlice + floorPlan.tesisatIds sync.
//
// Mevcut Command'lar (MainPanelCommand, MachineCommand, vb.) interactive add
// sırasında scene'e mesh ekliyor + simpleSupplySlice'a component yazıyor ama
// tesisatSlice'a dispatch etmiyor. tesisatSlice sadece JBZ save+reload
// migration ile dolduruluyor.
//
// Bu hook her layoutCounter değişiminde elektrik scene'ini tarar; tesisat
// tipinde olup tesisatSlice'ta olmayan mesh'leri tespit eder, addTesisat +
// addTesisatIdToFloorPlan dispatch eder. Tek noktada toplam — her Command'ı
// tek tek değiştirmeye gerek kalmaz.

import { useEffect } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import * as THREE from 'three';
import { addTesisat, selectAllTesisat } from '../../../redux/tesisatSlice';
import { addTesisatIdToFloorPlan } from '../../../redux/floorPlanSlice';
import { findContainingFloorPlanMesh, findFloorPlanMeshById, worldToMeshLocal } from '../utils/floorPlanMesh';
import { getActiveFloorPlanId, getGlobalDocumentBounds } from '../../../utils/getActiveFloorPlanId';

const TESISAT_TYPES = new Set([
  'socket',
  'mainPanel',
  'electricalPanel',
  'LightSwitch',
  'LightFixture',
  'machine',
  'supplyPoint',
  'cableShaft',
  // Buat tipleri (priz + aydinlatma hatlarinin dallanma noktalari):
  'socketBuat',
  'buatCircle',
  'buatSquare',
  'lightingBuat',
  'buatLightingCircle',
  'buatLightingSquare',
]);

function extractTesisatPayload(obj, scene) {
  const ud = obj.userData || {};
  // fpMesh-LOCAL koordinat hesapla (_v:3)
  let fpId = ud.floorPlanId || null;
  let localPos = null;
  let isLocal = false;

  const containingFpMesh = findContainingFloorPlanMesh(obj);
  if (containingFpMesh) {
    // Zaten fpMesh child — obj.position fpMesh-LOCAL
    localPos = { x: obj.position.x, y: obj.position.y, z: obj.position.z };
    if (!fpId) fpId = containingFpMesh.userData?.floorPlanId || null;
    isLocal = true;
  } else if (fpId && scene) {
    // Scene root'ta — fpMesh bul, WORLD → LOCAL donustur
    const fpMesh = findFloorPlanMeshById(scene, fpId);
    if (fpMesh) {
      const wp = new THREE.Vector3();
      obj.getWorldPosition(wp);
      localPos = worldToMeshLocal(fpMesh, { x: wp.x, y: wp.y, z: wp.z });
      isLocal = true;
    }
  }

  // Fallback: fpId yok (factory floorPlanId set etmemis) — WORLD pozisyondan detect et
  if (!fpId && !isLocal && scene) {
    const wp = new THREE.Vector3();
    obj.getWorldPosition(wp);
    const bounds = getGlobalDocumentBounds();
    const detectedFpId = getActiveFloorPlanId(wp, bounds);
    if (detectedFpId) {
      const fpMesh = findFloorPlanMeshById(scene, detectedFpId);
      if (fpMesh) {
        fpId = detectedFpId;
        localPos = worldToMeshLocal(fpMesh, { x: wp.x, y: wp.y, z: wp.z });
        isLocal = true;
      }
    }
  }

  if (!localPos) {
    // Fallback: fpMesh yok, WORLD kullan
    const wp = new THREE.Vector3();
    obj.getWorldPosition(wp);
    localPos = { x: wp.x, y: wp.y, z: wp.z };
  }

  const base = {
    id: ud.id,
    type: ud.type,
    position: localPos,
    rotation: obj.rotation?.z || 0,
    floorPlanId: fpId,
    _v: isLocal ? 3 : 2, // 3 = fpMesh-LOCAL, 2 = WORLD fallback
  };
  switch (ud.type) {
    case 'socket':
    case 'LightSwitch':
      return {
        ...base,
        subtype: ud.subtype || 'normal',
        rotateValue: ud.rotate || 0,
        stretchLength: ud.stretchLength || 0,
        color: ud.originalColor,
        dir: obj.scale?.y === -1 ? -1 : 1,
      };
    case 'LightFixture':
      return {
        ...base,
        // buildLightFixture createLightFixture'i symbol ile cagiriyor;
        // useFixture userData hem symbol hem fixtureType set ediyor — fallback ile
        // eski projeleri de destekle.
        symbol: ud.symbol || ud.fixtureType || 'normal',
        rotateValue: ud.rotate || 0,
        fixtureType: ud.fixtureType || ud.symbol || 'B2',
        power: ud.power || '10W',
        lampPower: ud.lampPower ?? 18,
        lampCount: ud.lampCount ?? 1,
        description: ud.description || '',
        color: ud.originalColor,
        dir: obj.scale?.y === -1 ? -1 : 1,
      };
    case 'mainPanel':
    case 'electricalPanel':
      return {
        ...base,
        dir: ud.dir || 1,
        symmetricalOffset: ud.symmetricalOffset || 0,
        panelKatPlanNo: ud.panelKatPlanNo || 0,
        panelInfo: ud.panelInfo,
      };
    case 'machine':
      return {
        ...base,
        machineType: ud.machineType || 'Bulaşık Makinesi',
        power: ud.power || '0',
        angle: ud.angle,
        dir: ud.dir || 1,
        color: ud.originalColor,
      };
    case 'supplyPoint':
      return {
        ...base,
        label: ud.name || 'Supply',
      };
    case 'cableShaft':
      return {
        ...base,
        corner1: ud.diagonalStart || ud.points?.[0],
        corner2: ud.diagonalEnd || ud.points?.[2],
        shaftGroupId: ud.shaftGroupId,
        floorPlanName: ud.floorPlanName,
      };
    case 'socketBuat':
    case 'buatCircle':
    case 'buatSquare':
    case 'lightingBuat':
    case 'buatLightingCircle':
    case 'buatLightingSquare': {
      // Buat factory'leri (singleCircleBuat/singleSquareBuat) color'i material'a
      // veriyor ama userData'ya yazmiyor — fallback olarak material'dan oku ki
      // buildBuat default 0x0045ff (mavi) ile renk degisikligi olmasin.
      const matColor = (() => {
        const mat = Array.isArray(obj.material) ? obj.material[0] : obj.material;
        return mat?.color?.getHex?.();
      })();
      return {
        ...base,
        buatType: ud.buatType || (ud.type?.toLowerCase().includes('square') ? 'square' : 'circle'),
        color: ud.color ?? ud.originalColor ?? matColor,
        socketUUID: ud.socketUUID || null,
        wireID: ud.wireID || null,
        panelID: ud.panelID || null,
      };
    }
    default:
      return base;
  }
}

export default function useSceneTesisatSync(scenesRef, tabId) {
  const scene = scenesRef?.current?.[tabId] || null;
  const items = useSelector(selectAllTesisat, shallowEqual);
  const layoutCounter = useSelector((state) => state.utils?.updateCounter ?? 0);
  const dispatch = useDispatch();

  useEffect(() => {
    if (!scene) return;
    const existingById = new Map(items.map(i => [i.id, i]));

    let synced = 0;
    scene.traverse((obj) => {
      const ud = obj.userData;
      if (!ud?.id || !ud?.type) return;
      if (!TESISAT_TYPES.has(ud.type)) return;

      const existing = existingById.get(ud.id);
      if (!existing) {
        const payload = extractTesisatPayload(obj, scene);
        if (!payload.id || !payload.type) return;
        dispatch(addTesisat(payload));
        if (payload.floorPlanId) {
          dispatch(addTesisatIdToFloorPlan({
            tabId,
            floorPlanId: payload.floorPlanId,
            itemId: payload.id,
          }));
        }
        synced += 1;
      }
    });
    if (typeof window !== 'undefined' && synced > 0) {
      window.__sceneSyncCount = (window.__sceneSyncCount || 0) + synced;
    }
  }, [scene, layoutCounter, items, dispatch]);
}
